import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Unit, Event, Group, DateFilter, DeleteConfirmation, GroupMember, UnitMember, MemberCompany, Company } from '../types';
import {
  Download,
  Trash2,
  Monitor,
  Activity,
  Folder,
  User as UserIcon,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Filter,
  ArrowUpDown,
  Plus,
  X,
  FolderPlus,
  ArrowRight,
  MinusCircle,
  Users,
  Pencil,
  Check,
  Eye,
  ArrowRightLeft,
  Inbox,
  AlertCircle,
  AlertTriangle,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import DateFilterComponent from './DateFilter';
import ConfirmDialog from './ConfirmDialog';
import CompanyManagement from './CompanyManagement';
import TransferModal from './TransferModal';
import TransfersInbox from './TransfersInbox';
import Modal from './ui/Modal';
import DashboardLayout, { DashboardSection } from './dashboard/DashboardLayout';
import OverviewSection from './dashboard/OverviewSection';
import { EventsOverTime } from './dashboard/Charts';
import {
  exportUserData,
  exportUnitsData,
  exportEventsData,
  exportGroupsData,
  exportAllData
} from '../utils/exportUtils';
import type { CompanyUserExportRow } from '../utils/exportUtils';
import { getEventDescription, getEventUserDisplay } from '../utils/eventUtils';
import { formatLockMode } from '../utils/lockModeUtils';


type SortConfig<T> = {
  key: keyof T;
  direction: 'ascending' | 'descending';
};

// Compact page list with ellipsis, e.g. [1, '…', 4, 5, 6, '…', 20]
const getPageNumbers = (current: number, total: number): Array<number | '…'> => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

const Dashboard: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState<DashboardSection>(() => {
    const saved = sessionStorage.getItem('lw_section');
    return (saved as DashboardSection) || 'overview';
  });
  const [companiesSummary, setCompaniesSummary] = useState({ count: 0, userCount: 0 });
  const [units, setUnits] = useState<Unit[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ startDate: null, endDate: null });
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');

  // States for table sorting and filtering
  const [unitFilter, setUnitFilter] = useState('');
  const [unitSortConfig, setUnitSortConfig] = useState<SortConfig<Unit> | null>(null);
  const [eventFilter, setEventFilter] = useState('');
  const [eventSortConfig, setEventSortConfig] = useState<SortConfig<Event> | null>(null);
  const [groupFilter, setGroupFilter] = useState('');
  const [groupSortConfig, setGroupSortConfig] = useState<SortConfig<Group> | null>(null);

  // States for event pagination
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 25;

  // Create group modal
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createGroupLoading, setCreateGroupLoading] = useState(false);

  // Manage group locks modal
  const [managingGroup, setManagingGroup] = useState<Group | null>(null);
  const [groupLockFilter, setGroupLockFilter] = useState('');

  // Group members (sharing) modal
  const [viewingMembersGroup, setViewingMembersGroup] = useState<Group | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [groupMemberCompanies, setGroupMemberCompanies] = useState<MemberCompany[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Unit members (sharing) modal
  const [viewingMembersUnit, setViewingMembersUnit] = useState<Unit | null>(null);
  const [viewingDetailsUnit, setViewingDetailsUnit] = useState<Unit | null>(null);
  const [unitMembers, setUnitMembers] = useState<UnitMember[]>([]);
  const [unitMemberCompanies, setUnitMemberCompanies] = useState<MemberCompany[]>([]);
  const [unitMembersLoading, setUnitMembersLoading] = useState(false);

  // Editable user name
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Refreshing
  const [refreshing, setRefreshing] = useState(false);

  // Full report export
  const [exportingAll, setExportingAll] = useState(false);

  const [confirmation, setConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {}
  });

  // Lock transfers
  const [transferUnits, setTransferUnits] = useState<Unit[] | null>(null);
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [showInbox, setShowInbox] = useState(false);
  const [incomingTransfers, setIncomingTransfers] = useState(0);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ msg, type });
    // Warnings/errors carry more important info, so keep them on screen longer.
    setTimeout(() => setToast(null), type === 'success' ? 4500 : 8000);
  };

  const refreshIncomingTransfers = useCallback(async () => {
    const res = await apiService.getIncomingTransfers();
    setIncomingTransfers(res.data?.length || 0);
  }, []);

  const loadData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [unitsResponse, groupsResponse] = await Promise.all([
        apiService.getUnits(),
        apiService.getGroups()
      ]);

      if (unitsResponse.success && unitsResponse.data) {
        setUnits(unitsResponse.data);
      }
      if (groupsResponse.success && groupsResponse.data) {
        // Units ya vienen incluidas desde el backend
        setGroups(groupsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      // Only load events when a unit is selected
      if (!selectedUnitId) {
        setEvents([]);
        return;
      }

      const startDate = dateFilter.startDate?.toISOString();
      const endDate = dateFilter.endDate?.toISOString();

      const eventsResponse = await apiService.getEvents(selectedUnitId, startDate, endDate);
      if (eventsResponse.success && eventsResponse.data) {
        // Filter events by unit and date
        let filteredEvents = eventsResponse.data.filter(event => event.unit_id === selectedUnitId);

        // Filter by date when date filters are selected
        if (dateFilter.startDate || dateFilter.endDate) {
          filteredEvents = filteredEvents.filter(event => {
            const eventDate = new Date(event.registered_at);
            const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
            const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

            if (startDate && eventDate < startDate) return false;
            if (endDate && eventDate > endDate) return false;
            return true;
          });
        }

        setEvents(filteredEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  }, [selectedUnitId, dateFilter.startDate, dateFilter.endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    refreshIncomingTransfers();
  }, [refreshIncomingTransfers]);

  useEffect(() => {
    sessionStorage.setItem('lw_section', activeSection);
  }, [activeSection]);

  // Companies summary for the Overview cards (cheap: counts only)
  useEffect(() => {
    apiService.getMyCompanies().then((res) => {
      if (res.success && res.data) {
        setCompaniesSummary({
          count: res.data.length,
          userCount: res.data.reduce((sum, c) => sum + (c.user_count || 0), 0),
        });
      }
    });
  }, []);

  // Set initial unit selection when units are loaded
  useEffect(() => {
    if (units.length > 0 && !selectedUnitId) {
      setSelectedUnitId(units[0].id);
    }
  }, [units, selectedUnitId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Memoized and sorted data
  const sortedAndFilteredUnits = React.useMemo(() => {
    const q = unitFilter.toLowerCase();
    let filtered = units.filter(unit =>
      [unit.name, unit.group_name, unit.mac_address]
        .some(val => (val ? String(val).toLowerCase().includes(q) : false))
    );

    if (unitSortConfig !== null) {
      filtered.sort((a, b) => {
        const aValue = a[unitSortConfig.key];
        const bValue = b[unitSortConfig.key];
        if (aValue == null) return unitSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return unitSortConfig.direction === 'ascending' ? 1 : -1;
        if (aValue < bValue) return unitSortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return unitSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [units, unitFilter, unitSortConfig]);

  // Candados transferibles visibles (solo los propios)
  const transferableVisibleUnits = React.useMemo(
    () => sortedAndFilteredUnits.filter((u) => u.owner_id === user?.id),
    [sortedAndFilteredUnits, user?.id]
  );
  const allVisibleSelected =
    transferableVisibleUnits.length > 0 &&
    transferableVisibleUnits.every((u) => selectedUnitIds.has(u.id));

  const toggleUnitSelection = (id: string) => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        transferableVisibleUnits.forEach((u) => next.delete(u.id));
      } else {
        transferableVisibleUnits.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const startBulkTransfer = () => {
    const selected = units.filter((u) => selectedUnitIds.has(u.id) && u.owner_id === user?.id);
    if (selected.length > 0) setTransferUnits(selected);
  };

  const requestSort = <T,>(key: keyof T, config: SortConfig<T> | null, setConfig: (config: SortConfig<T>) => void) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (config && config.key === key && config.direction === 'ascending') {
      direction = 'descending';
    }
    setConfig({ key, direction });
  };

  const sortIcon = <T,>(key: keyof T, config: SortConfig<T> | null) => {
    if (!config || config.key !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    }
    return config.direction === 'ascending'
      ? <ChevronUp className="h-3.5 w-3.5 ml-1 text-indigo-600" />
      : <ChevronDown className="h-3.5 w-3.5 ml-1 text-indigo-600" />;
  };

  const sortedAndFilteredEvents = React.useMemo(() => {
    const q = eventFilter.toLowerCase();
    let filtered = events.filter(event =>
      [getEventDescription(event), getEventUserDisplay(event)]
        .some(val => (val ? String(val).toLowerCase().includes(q) : false))
    );

    if (eventSortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        if (eventSortConfig.key === 'type') {
          aValue = getEventDescription(a);
          bValue = getEventDescription(b);
        } else {
          aValue = a[eventSortConfig.key];
          bValue = b[eventSortConfig.key];
        }
        if (aValue == null) return eventSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return eventSortConfig.direction === 'ascending' ? 1 : -1;
        if (aValue < bValue) return eventSortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return eventSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [events, eventFilter, eventSortConfig]);

  const sortedAndFilteredGroups = React.useMemo(() => {
    const q = groupFilter.toLowerCase();
    let filtered = groups.filter(group =>
      group.name.toLowerCase().includes(q) ||
      (group.units || []).some(u => u.name.toLowerCase().includes(q))
    );

    if (groupSortConfig !== null) {
      filtered.sort((a, b) => {
        const aValue = a[groupSortConfig.key];
        const bValue = b[groupSortConfig.key];
        if (aValue == null) return groupSortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return groupSortConfig.direction === 'ascending' ? 1 : -1;
        if (typeof aValue === 'object' || typeof bValue === 'object') return 0;
        if (aValue < bValue) return groupSortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return groupSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [groups, groupFilter, groupSortConfig]);


  const handleLogout = async () => {
    await logout();
  };

  const showConfirmation = (title: string, message: string, onConfirm: () => void) => {
    setConfirmation({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      },
      onCancel: () => setConfirmation(prev => ({ ...prev, isOpen: false }))
    });
  };

  const handleDeleteUnit = (unitId: string, unitName: string) => {
    showConfirmation(
      'Delete Unit',
      `Are you sure you want to delete the unit "${unitName}"? This action cannot be undone.`,
      async () => {
        try {
          await apiService.deleteUnit(unitId);
          setUnits(units.filter(u => u.id !== unitId));
          showToast(`Unit "${unitName}" deleted`);
        } catch (error) {
          console.error('Error deleting unit:', error);
          showToast('Could not delete the unit', 'error');
        }
      }
    );
  };

  const handleDeleteEvents = () => {
    if (!selectedUnitId) {
      showToast('Select a lock first', 'error');
      return;
    }

    const startDate = dateFilter.startDate?.toISOString();
    const endDate = dateFilter.endDate?.toISOString();
    const selectedUnit = units.find(u => u.id === selectedUnitId);
    const unitName = selectedUnit ? selectedUnit.name : 'selected unit';

    const dateRange = startDate && endDate
      ? `from ${format(new Date(startDate), 'dd/MM/yyyy', { locale: enUS })} to ${format(new Date(endDate), 'dd/MM/yyyy', { locale: enUS })}`
      : startDate
        ? `from ${format(new Date(startDate), 'dd/MM/yyyy', { locale: enUS })}`
        : endDate
          ? `until ${format(new Date(endDate), 'dd/MM/yyyy', { locale: enUS })}`
          : 'all events';

    showConfirmation(
      'Delete Events',
      `Are you sure you want to delete ${dateRange} for unit "${unitName}"? This action cannot be undone.`,
      async () => {
        try {
          await apiService.deleteEvents(selectedUnitId, startDate, endDate);
          await loadEvents();
          showToast('Events deleted');
        } catch (error) {
          console.error('Error deleting events:', error);
          showToast('Could not delete events', 'error');
        }
      }
    );
  };

  // Functions to handle event pagination
  const totalPages = Math.ceil(sortedAndFilteredEvents.length / eventsPerPage);
  const startIndex = (currentPage - 1) * eventsPerPage;
  const endIndex = startIndex + eventsPerPage;
  const currentEvents = sortedAndFilteredEvents.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Reset page when events change
  useEffect(() => {
    setCurrentPage(1);
  }, [events.length, dateFilter, eventFilter]);

  const handleDeleteGroup = (groupId: string, groupName: string) => {
    showConfirmation(
      'Delete Group',
      `Are you sure you want to delete the group "${groupName}"? This action cannot be undone.`,
      async () => {
        try {
          await apiService.deleteGroup(groupId);
          setGroups(groups.filter(g => g.id !== groupId));
          showToast(`Group "${groupName}" deleted`);
        } catch (error) {
          console.error('Error deleting group:', error);
          showToast('Could not delete the group', 'error');
        }
      }
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadEvents();
    await refreshIncomingTransfers();
    setRefreshing(false);
  };

  const handleExportAll = async () => {
    if (!user || exportingAll) return;
    setExportingAll(true);
    try {
      // 1) ALL events across every lock — single server-side export query.
      const allEventsRes = await apiService.getAllEvents();
      const allEvents: Event[] = allEventsRes.success && allEventsRes.data ? allEventsRes.data : [];
      // Server caps the events export; if we hit it, some rows are missing.
      // Keep this in sync with EVENTS_EXPORT_CAP in the backend.
      const EVENTS_EXPORT_CAP = 200000;
      const eventsTruncated = allEvents.length >= EVENTS_EXPORT_CAP;

      // 2) Companies + all their users — one export request per company.
      let companies: Company[] = [];
      const companyUsers: CompanyUserExportRow[] = [];
      const truncatedCompanies: string[] = [];
      const compRes = await apiService.getMyCompanies();
      if (compRes.success && compRes.data) {
        companies = compRes.data;
        const usersResults = await Promise.all(
          companies.map((c) => apiService.exportCompanyUsers(c.id))
        );
        companies.forEach((company, i) => {
          const res = usersResults[i];
          if (res.success && res.data) {
            companyUsers.push(
              ...res.data.users.map((u) => ({
                ...u,
                company_name: company.name,
                company_code: company.code,
              }))
            );
            if (res.data.truncated) truncatedCompanies.push(company.name);
          }
        });
      }

      exportAllData(user, units, allEvents, groups, companies, companyUsers);

      const warnings: string[] = [];
      if (truncatedCompanies.length > 0) {
        warnings.push(`company users were capped at 100,000 (${truncatedCompanies.join(', ')})`);
      }
      if (eventsTruncated) {
        warnings.push(`events were capped at ${EVENTS_EXPORT_CAP.toLocaleString('en-US')}`);
      }

      if (warnings.length > 0) {
        showToast(`Report generated, but ${warnings.join(' and ')}. Some rows are missing.`, 'warning');
      } else {
        showToast(
          `Full report generated · ${units.length} units, ${allEvents.length} events, ${companyUsers.length} company users`
        );
      }
    } catch (error) {
      console.error('Error generating full report:', error);
      showToast('Could not generate the full report', 'error');
    } finally {
      setExportingAll(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreateGroupLoading(true);
    try {
      const res = await apiService.createGroup(newGroupName.trim());
      if (res.success) {
        setNewGroupName('');
        setShowCreateGroup(false);
        await loadData();
      }
    } catch (error) {
      console.error('Error creating group:', error);
    } finally {
      setCreateGroupLoading(false);
    }
  };

  const handleAddUnitToGroup = async (unitId: string, groupId: string) => {
    try {
      const res = await apiService.addUnitToGroup(unitId, groupId);
      if (res.success) await loadData();
    } catch (error) {
      console.error('Error adding unit to group:', error);
    }
  };

  const handleRemoveUnitFromGroup = async (unitId: string) => {
    try {
      const res = await apiService.removeUnitFromGroup(unitId);
      if (res.success) await loadData();
    } catch (error) {
      console.error('Error removing unit from group:', error);
    }
  };

  // ── Group Members (Sharing) ────────────────────────────────

  // Deep-link to a company's detail view in the Company tab. Reuses the same
  // sessionStorage handshake CompanyManagement uses to restore its view, so the
  // company opens directly when that tab mounts.
  const goToCompany = (companyId: string) => {
    sessionStorage.setItem('lw_view', JSON.stringify({ kind: 'detail', companyId }));
    setViewingMembersGroup(null);
    setViewingMembersUnit(null);
    setActiveSection('companies');
  };

  const loadGroupMembers = async (group: Group) => {
    setViewingMembersGroup(group);
    setMembersLoading(true);
    try {
      const res = await apiService.getGroupMembers(group.id);
      if (res.success && res.data) {
        setGroupMembers(res.data.individuals);
        setGroupMemberCompanies(res.data.companies);
      } else {
        setGroupMembers([]);
        setGroupMemberCompanies([]);
      }
    } catch (error) {
      console.error('Error loading group members:', error);
      setGroupMembers([]);
      setGroupMemberCompanies([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRevokeGroupMember = (group: Group, member: GroupMember) => {
    showConfirmation(
      'Revoke Access',
      `Are you sure you want to revoke "${member.user_full_name}" access to the group "${group.name}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await apiService.removeGroupMember(group.id, member.id);
          if (res.success) {
            setGroupMembers(prev => prev.filter(m => m.id !== member.id));
            showToast(`Access revoked for ${member.user_full_name}`);
          } else {
            showToast('Could not revoke access', 'error');
          }
        } catch (error) {
          console.error('Error revoking member:', error);
          showToast('Could not revoke access', 'error');
        }
      }
    );
  };

  // ── Unit Members (Sharing) ─────────────────────────────────

  const loadUnitMembers = async (unit: Unit) => {
    setViewingMembersUnit(unit);
    setUnitMembersLoading(true);
    try {
      const res = await apiService.getUnitMembers(unit.id);
      if (res.success && res.data) {
        setUnitMembers(res.data.individuals);
        setUnitMemberCompanies(res.data.companies);
      } else {
        setUnitMembers([]);
        setUnitMemberCompanies([]);
      }
    } catch (error) {
      console.error('Error loading unit members:', error);
      setUnitMembers([]);
      setUnitMemberCompanies([]);
    } finally {
      setUnitMembersLoading(false);
    }
  };

  const handleRevokeUnitMember = (unit: Unit, member: UnitMember) => {
    showConfirmation(
      'Revoke Access',
      `Are you sure you want to revoke "${member.user_full_name}" access to the unit "${unit.name}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await apiService.removeUnitMember(unit.id, member.id);
          if (res.success) {
            setUnitMembers(prev => prev.filter(m => m.id !== member.id));
            showToast(`Access revoked for ${member.user_full_name}`);
          } else {
            showToast('Could not revoke access', 'error');
          }
        } catch (error) {
          console.error('Error revoking unit member:', error);
          showToast('Could not revoke access', 'error');
        }
      }
    );
  };

  // ── User Name Editing ──────────────────────────────────────

  const handleStartEditName = () => {
    setEditNameValue(user?.name || user?.full_name || '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editNameValue.trim()) return;
    setSavingName(true);
    try {
      const parts = editNameValue.trim().split(' ');
      const firstName = parts[0];
      const lastName = parts.slice(1).join(' ') || undefined;
      const res = await apiService.updateUserProfile({ first_name: firstName, last_name: lastName });
      if (res.success && res.data) {
        updateUser({ name: res.data.name, full_name: res.data.name });
      }
      setEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg shadow-indigo-500/30">
          <img src="/icon.png" alt="Lockwaste" className="w-full h-full object-cover" />
        </div>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-indigo-600"></div>
        <p className="text-sm font-medium text-slate-400 tracking-wide">Loading your data...</p>
      </div>
    );
  }

  const selectedUnitName = units.find(u => u.id === selectedUnitId)?.name;
  const sectionMeta: Record<DashboardSection, { title: string; subtitle?: string }> = {
    overview: { title: 'Overview', subtitle: 'Your fleet at a glance' },
    locks: { title: 'Locks', subtitle: `${units.length} total` },
    groups: { title: 'Groups', subtitle: `${groups.length} total` },
    events: { title: 'Events', subtitle: selectedUnitName ? `Lock: ${selectedUnitName}` : 'Select a lock' },
    companies: { title: 'Companies', subtitle: 'Manage company access' },
  };

  return (
    <>
      <ConfirmDialog confirmation={confirmation} />

      {/* Global toast (success / warning / error) */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[70] max-w-sm bg-white shadow-lg rounded-xl px-4 py-3 flex items-start gap-2 animate-slide-up border ${
            toast.type === 'error'
              ? 'border-red-200'
              : toast.type === 'warning'
              ? 'border-amber-200'
              : 'border-emerald-200'
          }`}
        >
          {toast.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          ) : toast.type === 'warning' ? (
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          )}
          <span className="text-sm text-slate-700">{toast.msg}</span>
        </div>
      )}

      <DashboardLayout
        active={activeSection}
        onSelect={setActiveSection}
        userName={user?.name || user?.full_name || 'User'}
        userEmail={user?.email}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        onLogout={handleLogout}
        badges={{ locks: incomingTransfers }}
        title={sectionMeta[activeSection].title}
        subtitle={sectionMeta[activeSection].subtitle}
      >
        {/* ─────────────── OVERVIEW ─────────────── */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <OverviewSection
              units={units}
              groups={groups}
              companiesCount={companiesSummary.count}
              companyUsersCount={companiesSummary.userCount}
              onNavigate={setActiveSection}
            />

            {/* Account */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-indigo-500" />
                    Account
                  </h2>
                  <button
                    onClick={() => user && exportUserData(user)}
                    className="w-full sm:w-auto flex justify-center items-center px-4 py-2 text-sm font-medium bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors shadow-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                      <UserIcon className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-500">Name</p>
                      {editingName ? (
                        <div className="flex items-center gap-2 mt-0.5">
                          <input
                            type="text"
                            value={editNameValue}
                            onChange={(e) => setEditNameValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                            className="flex-1 px-2 py-1 text-sm font-semibold text-slate-900 bg-white border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                            autoFocus
                          />
                          <button
                            onClick={handleSaveName}
                            disabled={savingName || !editNameValue.trim()}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Save"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingName(false)}
                            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{user?.name || user?.full_name || 'No name'}</p>
                          <button
                            onClick={handleStartEditName}
                            className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit name"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                      <Mail className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Email</p>
                      <p className="font-semibold text-slate-900">{user?.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Export complete data */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-lg border border-slate-700 overflow-hidden">
              <div className="px-8 py-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-0">
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-bold text-white mb-1">Export Complete System Data</h3>
                    <p className="text-sm text-slate-300">Download a comprehensive Excel report with units, all events from every lock, groups, companies and company users</p>
                  </div>
                  <button
                    onClick={handleExportAll}
                    disabled={exportingAll}
                    className="w-full sm:w-auto flex justify-center items-center px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-50 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {exportingAll ? (
                      <>
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-indigo-600 mr-2" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2 text-indigo-600" />
                        Generate Full Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <footer className="pt-2 pb-4 text-center space-y-2">
              <p className="text-xs text-slate-400 font-medium">
                LOCKWASTE Admin Portal &mdash; © {new Date().getFullYear()}. All rights reserved.
              </p>
              <p className="text-xs text-slate-400">
                Want to delete your account?{' '}
                <a href="/account-delete" className="underline hover:text-slate-500 transition-colors">
                  Click here
                </a>
              </p>
            </footer>
          </div>
        )}

        {/* ─────────────── LOCKS ─────────────── */}
        {activeSection === 'locks' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                    <Monitor className="h-5 w-5 mr-2 text-blue-500" />
                    Units ({units.length})
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => setShowInbox(true)}
                      className="relative w-full sm:w-auto flex justify-center items-center px-4 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                    >
                      <Inbox className="h-4 w-4 mr-2 text-indigo-500" />
                      Transfers
                      {incomingTransfers > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-indigo-600 rounded-full">
                          {incomingTransfers}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => exportUnitsData(units)}
                      className="w-full sm:w-auto flex justify-center items-center px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 border-b border-slate-100 bg-white">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Filter units..."
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    value={unitFilter}
                    onChange={(e) => setUnitFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="py-3 px-4 text-left w-10">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          disabled={transferableVisibleUnits.length === 0}
                          title="Select all your locks"
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-40"
                        />
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('name', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Name {sortIcon('name', unitSortConfig)}</button>
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('group_name', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Group {sortIcon('group_name', unitSortConfig)}</button>
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('battery', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Battery {sortIcon('battery', unitSortConfig)}</button>
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('updated_at', unitSortConfig, setUnitSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Update {sortIcon('updated_at', unitSortConfig)}</button>
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Shared With</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedAndFilteredUnits.map((unit) => (
                      <tr key={unit.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-3 px-4 w-10">
                          {unit.owner_id === user?.id && (
                            <input
                              type="checkbox"
                              checked={selectedUnitIds.has(unit.id)}
                              onChange={() => toggleUnitSelection(unit.id)}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-slate-900">{unit.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {unit.group_name ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                              {unit.group_name}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${unit.battery < 20 ? 'bg-red-100 text-red-800' : unit.battery < 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {unit.battery}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">{format(new Date(unit.updated_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</td>
                        <td className="py-3 px-4 text-sm">
                          {unit.owner_id === user?.id ? (
                            <button
                              onClick={() => loadUnitMembers(unit)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Users className="h-3.5 w-3.5" />
                              Members
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              Shared with me
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex gap-1">
                            <button
                              onClick={() => setViewingDetailsUnit(unit)}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="View details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteUnit(unit.id, unit.name)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Unit"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {sortedAndFilteredUnits.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500 text-sm">No units found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Transfer Modal */}
            {transferUnits && (
              <TransferModal
                units={transferUnits.map((u) => ({ id: u.id, name: u.name }))}
                onClose={() => setTransferUnits(null)}
                onSuccess={(msg) => {
                  setTransferUnits(null);
                  setSelectedUnitIds(new Set());
                  showToast(msg);
                  loadData(true);
                  refreshIncomingTransfers();
                }}
              />
            )}

            {/* Transfers Inbox */}
            {showInbox && (
              <TransfersInbox
                onClose={() => setShowInbox(false)}
                onChanged={() => {
                  loadData(true);
                  refreshIncomingTransfers();
                }}
              />
            )}

            {/* Floating selection bar */}
            {selectedUnitIds.size > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white border border-slate-200 shadow-lg rounded-full pl-5 pr-2 py-2">
                <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                  {selectedUnitIds.size} lock{selectedUnitIds.size === 1 ? '' : 's'} selected
                </span>
                <button
                  onClick={() => setSelectedUnitIds(new Set())}
                  className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-full transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={startBulkTransfer}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-1.5" />
                  Transfer
                </button>
              </div>
            )}

            {/* Unit Members Modal */}
            {viewingMembersUnit && (
              <Modal
                open
                onClose={() => setViewingMembersUnit(null)}
                title={`Shared with — ${viewingMembersUnit.name}`}
                icon={<Users className="h-4 w-4 text-blue-500" />}
                headerAccent="bg-blue-50/50"
                maxWidth="lg"
                bodyPadding={false}
              >
                <div>
                  {unitMembersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-blue-600" />
                    </div>
                  ) : unitMembers.length === 0 && unitMemberCompanies.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">
                      This unit is not shared with anyone
                    </div>
                  ) : (
                    <>
                      {unitMemberCompanies.length > 0 && (
                        <div>
                          <p className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Companies</p>
                          <div className="divide-y divide-slate-100">
                            {unitMemberCompanies.map((company) => (
                              <button
                                key={company.company_id}
                                onClick={() => goToCompany(company.company_id)}
                                className="w-full flex items-center justify-between px-6 py-3 text-left hover:bg-slate-50/50 transition-colors"
                                title="Manage in Company tab"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                                    <Building2 className="h-4 w-4 text-violet-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{company.company_name}</p>
                                    <p className="text-xs text-slate-400">
                                      Company · {company.user_count.toLocaleString()} {company.user_count === 1 ? 'user' : 'users'}
                                    </p>
                                  </div>
                                </div>
                                <span className="flex items-center gap-1 text-xs font-medium text-violet-600">
                                  Manage
                                  <ChevronRight size={16} />
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {unitMembers.length > 0 && (
                        <div>
                          {unitMemberCompanies.length > 0 && (
                            <p className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Individual users</p>
                          )}
                          <div className="divide-y divide-slate-100">
                            {unitMembers.map((member) => (
                              <div key={member.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                    <UserIcon className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{member.user_full_name}</p>
                                    <p className="text-xs text-slate-400">{member.email || 'No email'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {member.allow_settings_changes && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      Settings
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleRevokeUnitMember(viewingMembersUnit, member)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Revoke access"
                                  >
                                    <MinusCircle size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Modal>
            )}

            {/* Unit Details Modal */}
            {viewingDetailsUnit && (() => {
              let detailCoords: { latitude: number; longitude: number } | null = null;
              try {
                detailCoords = viewingDetailsUnit.coords ? JSON.parse(viewingDetailsUnit.coords) : null;
              } catch {
                detailCoords = null;
              }
              return (
                <Modal
                  open
                  onClose={() => setViewingDetailsUnit(null)}
                  title={viewingDetailsUnit.name}
                  icon={<Monitor className="h-4 w-4 text-indigo-500" />}
                  headerAccent="bg-indigo-50/50"
                  maxWidth="md"
                  bodyPadding={false}
                >
                  <div className="divide-y divide-slate-100">
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">MAC Address</span>
                      <span className="text-sm font-mono text-slate-800">{viewingDetailsUnit.mac_address}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Group</span>
                      <span className="text-sm font-medium text-slate-800">{viewingDetailsUnit.group_name || '—'}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Battery</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${viewingDetailsUnit.battery < 20 ? 'bg-red-100 text-red-800' : viewingDetailsUnit.battery < 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                        {viewingDetailsUnit.battery}%
                      </span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Temperature</span>
                      <span className="text-sm text-slate-800">{viewingDetailsUnit.temperature}°C</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Lock Mode</span>
                      <span className="text-sm text-slate-800">{formatLockMode(viewingDetailsUnit.accelerometer)}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Coordinates</span>
                      <span className="text-sm font-mono text-slate-800">
                        {detailCoords && typeof detailCoords.latitude === 'number' && typeof detailCoords.longitude === 'number'
                          ? `${detailCoords.latitude.toFixed(4)}, ${detailCoords.longitude.toFixed(4)}`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Container Type</span>
                      <span className="text-sm text-slate-800">{viewingDetailsUnit.container_type || '—'}</span>
                    </div>
                    <div className="px-6 py-3 flex justify-between items-center">
                      <span className="text-sm text-slate-500">Last Update</span>
                      <span className="text-sm text-slate-800">{format(new Date(viewingDetailsUnit.updated_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</span>
                    </div>
                    {viewingDetailsUnit.description && (
                      <div className="px-6 py-3">
                        <span className="text-sm text-slate-500 block mb-1">Description</span>
                        <span className="text-sm text-slate-800">{viewingDetailsUnit.description}</span>
                      </div>
                    )}
                  </div>
                </Modal>
              );
            })()}
          </div>
        )}

        {/* ─────────────── GROUPS ─────────────── */}
        {activeSection === 'groups' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                  <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                    <Folder className="h-5 w-5 mr-2 text-purple-500" />
                    Groups ({groups.length})
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCreateGroup(true)}
                      className="flex items-center px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm hover:shadow"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      New Group
                    </button>
                    <button
                      onClick={() => exportGroupsData(groups)}
                      className="flex items-center px-4 py-2 text-sm font-medium bg-white text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors shadow-sm"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      Export
                    </button>
                  </div>
                </div>
              </div>
              {showCreateGroup && (
                <div className="px-6 py-4 border-b border-slate-100 bg-purple-50/30">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-slate-600 mb-1.5">Group Name</label>
                      <input
                        type="text"
                        placeholder="Enter group name..."
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                      />
                    </div>
                    <button
                      onClick={handleCreateGroup}
                      disabled={createGroupLoading || !newGroupName.trim()}
                      className="px-4 py-2.5 text-sm font-semibold bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 shadow-sm"
                    >
                      {createGroupLoading ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      onClick={() => { setShowCreateGroup(false); setNewGroupName(''); }}
                      className="px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <div className="p-4 border-b border-slate-100 bg-white">
                <div className="relative max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Filter groups..."
                    className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium"
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('name', groupSortConfig, setGroupSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Name {sortIcon('name', groupSortConfig)}</button>
                      </th>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Units</th>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Shared With</th>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedAndFilteredGroups.map((group) => (
                      <tr key={group.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-4 px-6 text-sm font-semibold text-slate-800">{group.name}</td>
                        <td className="py-4 px-6 text-sm text-slate-600">
                          {group.units && group.units.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {group.units.map((unit, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                  {unit.name}
                                  {group.is_own === 1 ? (
                                    <button
                                      onClick={() => handleRemoveUnitFromGroup(unit.id)}
                                      className="ml-0.5 hover:text-red-600 transition-colors"
                                      title={`Remove ${unit.name} from group`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  ) : null}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No units</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-sm">
                          {group.is_own === 1 ? (
                            <button
                              onClick={() => loadGroupMembers(group)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                              title="View shared members"
                            >
                              <Users className="h-3.5 w-3.5" />
                              Members
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 italic">
                              <Users className="h-3 w-3" />
                              Shared with me
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-sm">
                          {group.is_own === 1 ? (
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => { setManagingGroup(group); setGroupLockFilter(''); }}
                                className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Manage locks in group"
                              >
                                <FolderPlus size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group.id, group.name)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Group"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {sortedAndFilteredGroups.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">No groups found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Group Members Modal */}
            {viewingMembersGroup && (
              <Modal
                open
                onClose={() => setViewingMembersGroup(null)}
                title={`Shared with — ${viewingMembersGroup.name}`}
                icon={<Users className="h-4 w-4 text-blue-500" />}
                headerAccent="bg-blue-50/50"
                maxWidth="lg"
                bodyPadding={false}
              >
                <div>
                  {membersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-blue-600" />
                    </div>
                  ) : groupMembers.length === 0 && groupMemberCompanies.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-sm">
                      This group is not shared with anyone
                    </div>
                  ) : (
                    <>
                      {groupMemberCompanies.length > 0 && (
                        <div>
                          <p className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Companies</p>
                          <div className="divide-y divide-slate-100">
                            {groupMemberCompanies.map((company) => (
                              <button
                                key={company.company_id}
                                onClick={() => goToCompany(company.company_id)}
                                className="w-full flex items-center justify-between px-6 py-3 text-left hover:bg-slate-50/50 transition-colors"
                                title="Manage in Company tab"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                                    <Building2 className="h-4 w-4 text-violet-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{company.company_name}</p>
                                    <p className="text-xs text-slate-400">
                                      Company · {company.user_count.toLocaleString()} {company.user_count === 1 ? 'user' : 'users'}
                                    </p>
                                  </div>
                                </div>
                                <span className="flex items-center gap-1 text-xs font-medium text-violet-600">
                                  Manage
                                  <ChevronRight size={16} />
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {groupMembers.length > 0 && (
                        <div>
                          {groupMemberCompanies.length > 0 && (
                            <p className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Individual users</p>
                          )}
                          <div className="divide-y divide-slate-100">
                            {groupMembers.map((member) => (
                              <div key={member.id} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                    <UserIcon className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{member.user_full_name}</p>
                                    <p className="text-xs text-slate-400">{member.email || 'No email'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {member.allow_settings_changes && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      Settings
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleRevokeGroupMember(viewingMembersGroup, member)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Revoke access"
                                  >
                                    <MinusCircle size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Modal>
            )}

            {/* Manage Group Locks Modal */}
            {managingGroup && (
              <Modal
                open
                onClose={() => setManagingGroup(null)}
                title={`Manage locks — ${managingGroup.name}`}
                icon={<FolderPlus className="h-4 w-4 text-purple-500" />}
                headerAccent="bg-purple-50/50"
                maxWidth="lg"
                bodyPadding={false}
              >
                <div className="p-4 border-b border-slate-100">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Filter locks..."
                      className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                      value={groupLockFilter}
                      onChange={(e) => setGroupLockFilter(e.target.value)}
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[55vh]">
                  {managingGroup.units && managingGroup.units.length > 0 && (
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">In this group</p>
                      {managingGroup.units
                        .filter(u => u.name.toLowerCase().includes(groupLockFilter.toLowerCase()) || u.mac_address?.toLowerCase().includes(groupLockFilter.toLowerCase()))
                        .map((unit) => (
                        <div key={unit.id} className="flex items-center justify-between py-2 px-3 mb-1 rounded-lg bg-purple-50 border border-purple-100">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{unit.name}</p>
                            <p className="text-xs text-slate-400 font-mono">{unit.mac_address}</p>
                          </div>
                          <button
                            onClick={async () => { await handleRemoveUnitFromGroup(unit.id); setManagingGroup(prev => prev ? {...prev, units: prev.units.filter(u => u.id !== unit.id)} : null); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <MinusCircle className="h-3.5 w-3.5" /> Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="px-4 pt-3 pb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Available locks (no group)</p>
                    {units
                      .filter(u => !u.group_id && (u.name.toLowerCase().includes(groupLockFilter.toLowerCase()) || u.mac_address?.toLowerCase().includes(groupLockFilter.toLowerCase())))
                      .map((unit) => (
                      <div key={unit.id} className="flex items-center justify-between py-2 px-3 mb-1 rounded-lg bg-slate-50 border border-slate-100">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{unit.name}</p>
                          <p className="text-xs text-slate-400 font-mono">{unit.mac_address}</p>
                        </div>
                        <button
                          onClick={async () => { await handleAddUnitToGroup(unit.id, managingGroup.id); setManagingGroup(prev => prev ? {...prev, units: [...prev.units, unit]} : null); }}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                        >
                          <ArrowRight className="h-3.5 w-3.5" /> Add
                        </button>
                      </div>
                    ))}
                    {units.filter(u => !u.group_id).length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-4">All locks are already in a group</p>
                    )}
                  </div>
                </div>
              </Modal>
            )}
          </div>
        )}

        {/* ─────────────── EVENTS ─────────────── */}
        {activeSection === 'events' && (
          <div className="space-y-6">
            {selectedUnitId && sortedAndFilteredEvents.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <h2 className="text-sm font-bold text-slate-700">Activity over time</h2>
                </div>
                <div className="p-4 h-64">
                  <EventsOverTime events={sortedAndFilteredEvents} />
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-emerald-500" />
                      Events ({events.length})
                    </h2>
                    {selectedUnitId && (
                      <p className="text-sm text-slate-500 mt-1.5 flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2"></span>
                        Showing events for: <span className="font-medium text-slate-700 ml-1">{selectedUnitName}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={() => exportEventsData(events, selectedUnitName || 'selected unit')}
                      className="flex justify-center items-center px-4 py-2 text-sm font-medium bg-white text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors shadow-sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </button>
                    <button
                      onClick={handleDeleteEvents}
                      className="flex justify-center items-center px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm hover:shadow"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Events
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-b border-slate-100">
                <div className="flex flex-col gap-6">
                  <div className="w-full space-y-2">
                    <label className="text-sm font-semibold text-slate-600 ml-1">Date Range Filter</label>
                    <div className="bg-slate-50 p-2 rounded-xl border border-slate-200/60">
                      <DateFilterComponent dateFilter={dateFilter} onDateFilterChange={setDateFilter} />
                    </div>
                  </div>

                  <div className="w-full space-y-2">
                    <label className="text-sm font-semibold text-slate-600 ml-1">Select a lock to view its events</label>
                    <div className="relative">
                      <select
                        value={selectedUnitId}
                        onChange={(e) => setSelectedUnitId(e.target.value)}
                        className="w-full pl-4 pr-10 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 appearance-none shadow-sm transition-all"
                      >
                        <option value="">— Select a lock —</option>
                        {units.map((unit) => (
                          <option key={unit.id} value={unit.id}>{unit.name}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                        <ChevronRight className="h-4 w-4 rotate-90" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-b border-slate-100 bg-white">
                <div className="relative max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Filter className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search specific events..."
                    className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('registered_at', eventSortConfig, setEventSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Date {sortIcon('registered_at', eventSortConfig)}</button>
                      </th>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <button onClick={() => requestSort('type', eventSortConfig, setEventSortConfig)} className="flex items-center hover:text-indigo-600 transition-colors">Event Details {sortIcon('type', eventSortConfig)}</button>
                      </th>
                      <th className="py-3 px-6 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-3.5 px-6 text-sm text-slate-500 whitespace-nowrap">{format(new Date(event.registered_at), 'dd/MM/yyyy HH:mm', { locale: enUS })}</td>
                        <td className="py-3.5 px-6 text-sm text-slate-700 font-medium">{getEventDescription(event)}</td>
                        <td className="py-3.5 px-6 text-sm text-slate-500">{getEventUserDisplay(event)}</td>
                      </tr>
                    ))}
                    {currentEvents.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-slate-500 text-sm">
                          {selectedUnitId
                            ? 'No events found matching your criteria'
                            : 'Select a lock above to view its events'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                  <span className="text-sm font-medium text-slate-500">
                    Showing <span className="text-slate-900">{startIndex + 1}</span> to <span className="text-slate-900">{Math.min(endIndex, sortedAndFilteredEvents.length)}</span> of <span className="text-slate-900">{sortedAndFilteredEvents.length}</span> entries
                  </span>
                  <div className="flex items-center space-x-1.5">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="flex items-center px-3 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    {getPageNumbers(currentPage, totalPages).map((p, i) =>
                      p === '…' ? (
                        <span key={`gap-${i}`} className="px-2 text-sm text-slate-400 select-none">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p as number)}
                          aria-current={p === currentPage ? 'page' : undefined}
                          className={`min-w-[36px] px-3 py-2 text-sm font-medium rounded-lg border transition-colors shadow-sm ${
                            p === currentPage
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="flex items-center px-3 py-2 text-sm font-medium bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─────────────── COMPANIES ─────────────── */}
        {activeSection === 'companies' && <CompanyManagement />}
      </DashboardLayout>
    </>
  );
};

export default Dashboard;
